import { Outlet, Navigate, Link } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { TopBar } from '../components/layout/TopBar'
import { TickerTape } from '../components/market/TickerTape'
import { StatusBar } from '../components/layout/StatusBar'
import { MobileBottomNav } from '../components/layout/MobileBottomNav'
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts'
import { useAlertChecker } from '../hooks/useAlertChecker'
import { OnboardingModal } from '../components/ui/OnboardingModal'
import { HotkeyModal } from '../components/ui/HotkeyModal'
import { CookieConsentBanner } from '../components/ui/CookieConsentBanner'
import { useTerminalStore } from '../store/terminalStore'
import { useMobile } from '../hooks/useMobile'

function LayoutInner() {
  useKeyboardShortcuts()
  useAlertChecker()
  const { onboardingDone, hotkeyModalOpen, setHotkeyModalOpen } = useTerminalStore()
  const isMobile = useMobile()
  return (
    <div className="flex flex-col h-full bg-bb-bg overflow-hidden">
      {/* WCAG 2.4.1: skip-to-content link for keyboard users */}
      <a href="#bb-main-content" className="bb-skip-link">Skip to main content</a>
      {!onboardingDone && <OnboardingModal />}
      <HotkeyModal open={hotkeyModalOpen} onClose={() => setHotkeyModalOpen(false)} />
      <CookieConsentBanner />
      {!isMobile && <TickerTape />}
      <TopBar />
      <main
        id="bb-main-content"
        tabIndex={-1}
        className="flex-1 min-h-0 overflow-y-auto"
        style={isMobile ? { paddingBottom: 'calc(3.5rem + env(safe-area-inset-bottom))' } : undefined}
        aria-label="Main content"
      >
        <Outlet />
      </main>
      {isMobile ? (
        <MobileBottomNav />
      ) : (
        <>
          <StatusBar />
          {/* Legal disclaimer footer */}
          <div className="shrink-0 px-4 py-0.5 bg-bb-surface border-t border-bb-border/40 flex items-center justify-between">
            <p className="text-2xs text-bb-disabled">
              Not financial advice. All data for informational purposes only.{' '}
              <Link to="/disclaimer" className="hover:text-bb-subtle underline-offset-2 hover:underline">Disclaimer</Link>
            </p>
            <div className="flex gap-3">
              <Link to="/terms"       className="text-2xs text-bb-disabled hover:text-bb-subtle">Terms</Link>
              <Link to="/privacy"     className="text-2xs text-bb-disabled hover:text-bb-subtle">Privacy</Link>
              <Link to="/disclaimer"  className="text-2xs text-bb-disabled hover:text-bb-subtle">Disclaimer</Link>
              <Link to="/do-not-sell" className="text-2xs text-bb-disabled hover:text-bb-subtle">Do Not Sell My Data</Link>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="flex flex-col h-full bg-bb-bg overflow-hidden animate-pulse">
      {/* ticker tape */}
      <div className="h-7 bg-bb-surface border-b border-bb-border" />
      {/* top bar */}
      <div className="h-10 bg-bb-surface border-b border-bb-border flex items-center px-4 gap-3">
        <div className="w-24 h-4 rounded bg-bb-border" />
        <div className="w-16 h-4 rounded bg-bb-border" />
        <div className="w-20 h-4 rounded bg-bb-border" />
      </div>
      {/* main content area */}
      <div className="flex-1 p-3 grid grid-cols-3 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="bg-bb-surface rounded border border-bb-border h-48" />
        ))}
      </div>
      {/* status bar */}
      <div className="h-6 bg-bb-surface border-t border-bb-border" />
    </div>
  )
}

export function TerminalLayout() {
  const { session, loading } = useAuthStore()

  if (loading) return <LoadingSkeleton />
  if (!session) return <Navigate to="/login" replace />

  return <LayoutInner />
}
